import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, deleteDoc, doc, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { listEvents } from '../services/eventsService';
import { getLayoutPublic } from '../services/layoutsService';
import { listReservationLogs, listReservationLogTypesByEvent } from '../services/reservationLogsService';
import { clearExpiredHolds, clearExpiredPendingReservations } from '../services/reservationsService';
import { purgeRemovedCartItemsForEvent, countRemovedCartItemsForEvent } from '../services/cartItemsService';
import { purgeAllCollections, countCollectionDocuments, purgeCollectionsForEvent } from '../services/maintenanceService';
import { purgeStaleSessionStates } from '../services/sessionService';
import { getUserProfile } from '../services/profilesService';
import { getVendor } from '../services/vendorsService';
import { useAuth } from '../context/AuthContext';
import { db } from '../../sdk_initialization';
import './ProfilePage.css';

const formatTs = (ts) => {
  try {
    if (!ts) return '';
    if (typeof ts.toMillis === 'function') return new Date(ts.toMillis()).toLocaleString();
    if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
    const d = new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toLocaleString();
  } catch { return ''; }
};

const normalizeNamePart = (value) => String(value || '').trim().toLowerCase();

const toMillis = (ts) => {
  try {
    if (!ts) return 0;
    if (typeof ts?.toMillis === 'function') return ts.toMillis();
    if (typeof ts?.seconds === 'number') return ts.seconds * 1000;
    const parsed = new Date(ts).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
};

const ReservationLogsViewer = () => {
  const COLLECTIONS_TO_COUNT = ['boothReservations', 'cartItems', 'reservationLogs', 'sessionStates', 'transactions', 'attendees', 'attendeeCapacity', 'boothApplications', 'checkins', 'registrations'];
  const navigate = useNavigate();
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventChoices, setEventChoices] = useState([]);
  const [eventId, setEventId] = useState('');
  const [layoutId, setLayoutId] = useState('');
  const [typesFilter, setTypesFilter] = useState([]); // array of selected types
  const [typeOptions, setTypeOptions] = useState([]); // dynamic, event-scoped list of types
  const [boothNumberFilter, setBoothNumberFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [contactFilter, setContactFilter] = useState('');
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [error, setError] = useState('');
  const [actorsLoading, setActorsLoading] = useState(false);
  const [profileMap, setProfileMap] = useState({}); // { uid: { firstName, lastName, vendorId, ... } }
  const [vendorMap, setVendorMap] = useState({});   // { vendorId: { name, ... } }

  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Load events (like booths admin) excluding archived and only with published layout
  // Apply role-aware filtering: Admin sees all, EventAdmin checks `eventAdmins`, Organizer checks `organizers`.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setEventsLoading(true);
        const all = await listEvents();
        const havingLayout = (all || []).filter(ev => ev?.layoutId && (ev?.status || '').toLowerCase() !== 'archived');

        // Role-aware filter
        let filteredHaving = havingLayout;
        try {
          if (currentUser && currentUser.uid) {
            try {
              const prof = await getUserProfile(currentUser.uid);
              const uidLower = String(currentUser.uid || '').toLowerCase();
              if (prof?.userType === 'EventAdmin') {
                filteredHaving = (havingLayout || []).filter(ev => Array.isArray(ev.eventAdmins) && ev.eventAdmins.some(a => String(a || '').toLowerCase() === uidLower));
              } else if (prof?.userType === 'Organizer') {
                filteredHaving = (havingLayout || []).filter(ev => Array.isArray(ev.organizers) && ev.organizers.some(a => String(a || '').toLowerCase() === uidLower));
              } else if (prof?.userType !== 'Admin') {
                filteredHaving = [];
              }
            } catch (profileErr) {
              filteredHaving = [];
            }
          }
        } catch (e) {
          filteredHaving = [];
        }

        const entries = [];
        for (const ev of (filteredHaving || [])) {
          try {
            const ly = await getLayoutPublic(ev.layoutId);
            if ((ly?.status || '').toLowerCase() !== 'published') continue;
            entries.push({ eventId: ev.id, eventName: ev.name || ev.id, layoutId: ly.id, layoutName: ly.name || ly.title || ly.id });
          } catch {}
        }
        entries.sort((a,b) => String(a.eventName).localeCompare(String(b.eventName)));
        if (!cancelled) {
          setEventChoices(entries);
          // Do not auto-select an event on page load; require explicit user selection
          setEventId('');
          setLayoutId('');
        }
      } catch (e) {
        if (!cancelled) setError('Failed to load events');
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  const loadLogs = React.useCallback(async () => {
    if (!eventId || !layoutId) { setLogs([]); return; }
    setLoadingLogs(true);
    setError('');
    try {
      // Do not filter by boothId at query time; we'll filter by booth number on the client
      const data = await listReservationLogs({ eventId, layoutId, types: typesFilter, max: 200 });
      setLogs(data);
    } catch (e) {
      setError(e?.message || 'Failed to load logs');
    } finally {
      setLoadingLogs(false);
    }
  }, [eventId, layoutId, typesFilter]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // Load available log types for the selected event and keep type filter valid.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!eventId) {
        if (!cancelled) {
          setTypeOptions([]);
          setTypesFilter([]);
        }
        return;
      }
      try {
        const types = await listReservationLogTypesByEvent({ eventId, max: 2000 });
        if (cancelled) return;
        setTypeOptions(types);
        setTypesFilter(prev => prev.filter(t => types.includes(t)));
      } catch {
        if (!cancelled) {
          setTypeOptions([]);
          setTypesFilter([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  // Load profiles/vendors for the actors present in the logs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!logs || logs.length === 0) { setProfileMap({}); setVendorMap({}); return; }
      try {
        setActorsLoading(true);
        // Determine actor profile ids: prefer triggeredByProfileId, else previousProfileId
        const uids = Array.from(new Set(
          logs.map(l => l.triggeredByProfileId || l.previousProfileId).filter(Boolean)
        ));
        if (uids.length === 0) { setProfileMap({}); setVendorMap({}); return; }
        // Fetch profiles
        const pairs = await Promise.all(uids.map(async uid => {
          try { const p = await getUserProfile(uid); return [uid, p]; } catch { return [uid, null]; }
        }));
        if (cancelled) return;
        const pMap = {};
        const vendorIds = new Set();
        pairs.forEach(([uid, prof]) => {
          if (prof) {
            pMap[uid] = prof;
            if (prof.vendorId) vendorIds.add(prof.vendorId);
          }
        });
        // Also collect any vendorIds referenced directly in the logs (previousVendorId or metadata.vendorId)
        (logs || []).forEach( l => {
          const directVid = l.vendorId || l.previousVendorId || (l.metadata && l.metadata.vendorId) || null;
          if (directVid) vendorIds.add(directVid);
        });
        
        // Fetch vendors
        const vPairs = await Promise.all(Array.from(vendorIds).map(async vid => {
          try { const v = await getVendor(vid); return [vid, v]; } catch { return [vid, null]; }
        }));
        if (cancelled) return;
        const vMap = {};
        vPairs.forEach(([vid, ven]) => { if (ven) vMap[vid] = ven; });
        setProfileMap(pMap);
        setVendorMap(vMap);
      } catch (e) {
        // Non-blocking; ignore
      } finally {
        if (!cancelled) setActorsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [logs]);

  const toggleType = (t) => {
    setTypesFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const filteredLogs = useMemo(() => {
    const bnNeedle = (boothNumberFilter || '').trim().toLowerCase();
    const vNeedle = (vendorFilter || '').trim().toLowerCase();
    const cNeedle = (contactFilter || '').trim().toLowerCase();

    const nameOfVendor = (uid) => {
      if (!uid) return '';
      const prof = profileMap[uid];
      if (!prof) return '';
      const vendor = prof.vendorId ? vendorMap[prof.vendorId] : null;
      return (vendor?.name || '').toLowerCase();
    };
    const nameOfContact = (uid) => {
      if (!uid) return '';
      const prof = profileMap[uid];
      if (!prof) return '';
      const first = (prof.firstName || '').trim();
      const last = (prof.lastName || '').trim();
      const full = [first, last].filter(Boolean).join(' ');
      return (full || prof.email || '').toLowerCase();
    };
    const boothNumOf = (l) => {
      const bn = (l.metadata && (l.metadata.boothNumber || l.metadata.boothNo)) || l.boothNumber || l.boothId;
      return String(bn || '').toLowerCase();
    };

    return (logs || []).filter(l => {
      const actorUid = l.triggeredByProfileId || l.previousProfileId || '';
      if (bnNeedle && !boothNumOf(l).includes(bnNeedle)) return false;
      if (vNeedle && !nameOfVendor(actorUid).includes(vNeedle)) return false;
      if (cNeedle && !nameOfContact(actorUid).includes(cNeedle)) return false;
      return true;
    });
  }, [logs, boothNumberFilter, vendorFilter, contactFilter, profileMap, vendorMap]);

  const [clearing, setClearing] = useState(false);
  const [clearResult, setClearResult] = useState('');
  const [removedCount, setRemovedCount] = useState(null);
  const [purgingAll, setPurgingAll] = useState(false);
  const [purgeAllResult, setPurgeAllResult] = useState('');
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [collectionCounts, setCollectionCounts] = useState(null);
  const [selectedCollections, setSelectedCollections] = useState({
    boothReservations: false,
    cartItems: false,
    reservationLogs: false,
    sessionStates: false,
    transactions: false,
    attendees: false,
    attendeeCapacity: false,
    boothApplications: false,
    checkins: false,
    registrations: false
  });
  const [purgingStaleSessions, setPurgingStaleSessions] = useState(false);
  const [staleSessionResult, setStaleSessionResult] = useState('');
  const [duplicateActionBusy, setDuplicateActionBusy] = useState(false);
  const [duplicateActionResult, setDuplicateActionResult] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!eventId) { setRemovedCount(null); return; }
        const n = await countRemovedCartItemsForEvent({ eventId });
        if (!cancelled) setRemovedCount(n);
      } catch {
        if (!cancelled) setRemovedCount(null);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);


  // Load current user profile to determine Admin privileges
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser?.uid) return;
      try {
        const prof = await getUserProfile(currentUser.uid);
        if (!cancelled) {
          setUserProfile(prof);
          setIsAdmin(!!(prof && prof.userType === 'Admin'));
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Load event-scoped collection counts for purge all display
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!eventId) {
          if (!cancelled) setCollectionCounts(null);
          return;
        }
        const counts = await countCollectionDocuments({ collections: COLLECTIONS_TO_COUNT, eventId });
        if (!cancelled) setCollectionCounts(counts);
      } catch {
        if (!cancelled) setCollectionCounts(null);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  const handlePurgeRemovedCarts = async () => {
    if (!eventId) return;
    setClearing(true);
    setClearResult('');
    try {
      const n = await purgeRemovedCartItemsForEvent({ eventId });
      setClearResult(n === 0 ? 'No cart items to purge.' : `Purged ${n} cart item${n === 1 ? '' : 's'} (removed/expired/old checkedout).`);
      try { const c = await countRemovedCartItemsForEvent({ eventId }); setRemovedCount(c); } catch {}
      await loadLogs();
    } catch (e) {
      setClearResult(e?.message || 'Failed to purge cart items');
    } finally {
      setClearing(false);
    }
  };

  const handlePurgeAllCollections = async () => {
    setShowPurgeConfirm(false);
    setPurgingAll(true);
    setPurgeAllResult('');
    try {
      // Only purge selected collections
      const toPurge = Object.entries(selectedCollections)
        .filter(([_, isSelected]) => isSelected)
        .map(([col, _]) => col);
      
      if (toPurge.length === 0) {
        setPurgeAllResult('No collections selected for purge.');
        return;
      }

      // Perform event-scoped purge (only delete docs that match the selected eventId)
      if (!eventId) {
        setPurgeAllResult('No event selected - cannot perform event-scoped purge.');
        return;
      }
      const results = await purgeCollectionsForEvent({ collections: toPurge, eventId });
      const summary = Object.entries(results)
        .map(([col, count]) => `${col}: ${typeof count === 'object' ? count.error : count}`)
        .join(', ');
      setPurgeAllResult(`Purged collections for event ${eventId}: ${summary}`);
      // Refresh event-scoped counts and logs (include attendeeCapacity)
      try {
        const counts = await countCollectionDocuments({ collections: COLLECTIONS_TO_COUNT, eventId });
        setCollectionCounts(counts);
      } catch {}
      await loadLogs();
    } catch (e) {
      setPurgeAllResult(e?.message || 'Failed to purge collections');
    } finally {
      setPurgingAll(false);
    }
  };

  const handlePurgeStaleSessions = async () => {
    setPurgingStaleSessions(true);
    setStaleSessionResult('');
    try {
      const count = await purgeStaleSessionStates({ olderThanMinutes: 30 });
      setStaleSessionResult(count === 0 ? 'No stale sessions found.' : `Purged ${count} stale session${count === 1 ? '' : 's'}.`);
      // Refresh event-scoped collection counts
      try {
        const counts = await countCollectionDocuments({ collections: COLLECTIONS_TO_COUNT, eventId });
        setCollectionCounts(counts);
      } catch {}
    } catch (e) {
      setStaleSessionResult(e?.message || 'Failed to purge stale sessions');
    } finally {
      setPurgingStaleSessions(false);
    }
  };

  const handleMarkDuplicateAttendees = async () => {
    if (!eventId) return;
    setDuplicateActionBusy(true);
    setDuplicateActionResult('');
    try {
      const attendeesSnap = await getDocs(query(collection(db, 'attendees'), where('eventId', '==', eventId)));
      const attendeeDocs = attendeesSnap.docs.map((snap) => ({ id: snap.id, ref: snap.ref, ...snap.data() }));

      const groups = new Map();
      attendeeDocs.forEach((attendee) => {
        const vendorId = String(attendee?.vendorId || '').trim();
        const firstName = normalizeNamePart(attendee?.firstName);
        const lastName = normalizeNamePart(attendee?.lastName);
        if (!vendorId || !firstName || !lastName) return;
        const key = `${vendorId}::${firstName}::${lastName}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(attendee);
      });

      const keepIds = new Set();
      const duplicateIds = new Set();
      groups.forEach((group) => {
        if (!Array.isArray(group) || group.length === 0) return;
        const sorted = group.slice().sort((a, b) => {
          const aPrimary = a?.isPrimary === true ? 0 : 1;
          const bPrimary = b?.isPrimary === true ? 0 : 1;
          if (aPrimary !== bPrimary) return aPrimary - bPrimary;
          const createdDiff = toMillis(a?.createdAt) - toMillis(b?.createdAt);
          if (createdDiff !== 0) return createdDiff;
          return String(a?.id || '').localeCompare(String(b?.id || ''));
        });

        keepIds.add(sorted[0].id);
        if (sorted.length > 1) {
          sorted.slice(1).forEach((attendee) => duplicateIds.add(attendee.id));
        }
      });

      let batch = writeBatch(db);
      let batchCount = 0;
      let updatedCount = 0;
      let duplicateGroupCount = 0;

      groups.forEach((group) => {
        if (Array.isArray(group) && group.length > 1) duplicateGroupCount += 1;
      });

      const commitBatch = async () => {
        if (batchCount === 0) return;
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
      };

      for (const attendee of attendeeDocs) {
        const shouldDelete = duplicateIds.has(attendee.id);
        const nextDeleteValue = shouldDelete;
        const currentDeleteValue = attendee?.delete === true;
        if (currentDeleteValue === nextDeleteValue) continue;
        batch.update(attendee.ref, { delete: nextDeleteValue });
        batchCount += 1;
        updatedCount += 1;
        if (batchCount >= 400) {
          await commitBatch();
        }
      }
      await commitBatch();

      const markedCount = duplicateIds.size;
      if (duplicateGroupCount === 0) {
        setDuplicateActionResult('No duplicate attendees found for the selected event.');
      } else {
        setDuplicateActionResult(`Marked ${markedCount} duplicate attendee${markedCount === 1 ? '' : 's'} across ${duplicateGroupCount} duplicate name group${duplicateGroupCount === 1 ? '' : 's'}. ${updatedCount} record${updatedCount === 1 ? '' : 's'} updated.`);
      }
      try {
        const counts = await countCollectionDocuments({ collections: COLLECTIONS_TO_COUNT, eventId });
        setCollectionCounts(counts);
      } catch {}
    } catch (e) {
      setDuplicateActionResult(e?.message || 'Failed to mark duplicate attendees');
    } finally {
      setDuplicateActionBusy(false);
    }
  };

  const handleDeleteDuplicateAttendees = async () => {
    if (!eventId) return;
    setDuplicateActionBusy(true);
    setDuplicateActionResult('');
    try {
      const attendeesSnap = await getDocs(query(collection(db, 'attendees'), where('eventId', '==', eventId)));
      const marked = attendeesSnap.docs.filter((snap) => snap.data()?.delete === true);
      if (marked.length === 0) {
        setDuplicateActionResult('No marked duplicate attendees found for the selected event.');
        return;
      }

      if (!window.confirm(`Delete ${marked.length} marked duplicate attendee record${marked.length === 1 ? '' : 's'} for this event? This cannot be undone.`)) {
        return;
      }

      for (let index = 0; index < marked.length; index += 1) {
        await deleteDoc(doc(db, 'attendees', marked[index].id));
      }

      setDuplicateActionResult(`Deleted ${marked.length} marked duplicate attendee record${marked.length === 1 ? '' : 's'}.`);
      try {
        const counts = await countCollectionDocuments({ collections: COLLECTIONS_TO_COUNT, eventId });
        setCollectionCounts(counts);
      } catch {}
    } catch (e) {
      setDuplicateActionResult(e?.message || 'Failed to delete marked duplicate attendees');
    } finally {
      setDuplicateActionBusy(false);
    }
  };

  return (
    <div className="profile-container profile-wide-container">
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div style={{ flex: 1 }} />
        <button type="button" className="cancel-btn" onClick={() => navigate('/')}>Close</button>
      </div>
      <h2 className="profile-title">Admin: Reservation & Cart Logs</h2>
      <div className="profile-grid" style={{ marginBottom: 12 }}>
        <div>
          <label className="profile-label">Event</label>
          <select className="profile-input" value={eventId} onChange={e => {
            const val = e.target.value;
            setEventId(val);
            const opt = eventChoices.find(o => o.eventId === val);
            if (opt) setLayoutId(opt.layoutId);
          }} disabled={eventsLoading || eventChoices.length === 0}>
            {eventsLoading && <option value="">Loading events…</option>}
            {!eventsLoading && eventChoices.length === 0 && <option value="">No events</option>}
            {!eventsLoading && eventChoices.length > 0 && (
              <>
                <option value="" disabled>Select an event…</option>
                {eventChoices.map(e => <option key={e.eventId} value={e.eventId}>{e.eventName}</option>)}
              </>
            )}
          </select>
          {eventId && <div className="profile-hint">Layout ID: {layoutId}</div>}
        </div>
        <div>
          <label className="profile-label">Booth Number (optional)</label>
          <input className="profile-input" value={boothNumberFilter} onChange={e => setBoothNumberFilter(e.target.value)} placeholder="e.g. 123 or A01" />
        </div>
        <div>
          <label className="profile-label">Vendor (optional)</label>
          <input className="profile-input" value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} placeholder="Search vendor name" />
        </div>
        <div>
          <label className="profile-label">Contact (optional)</label>
          <input className="profile-input" value={contactFilter} onChange={e => setContactFilter(e.target.value)} placeholder="Search contact name/email" />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label className="profile-label">Log Types</label>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {typeOptions.length === 0 ? (
              <span className="profile-hint">No log types found for selected event.</span>
            ) : (
              typeOptions.map(t => (
                <label key={t} style={{ fontSize:12, display:'flex', gap:4, alignItems:'center' }}>
                  <input type="checkbox" checked={typesFilter.includes(t)} onChange={() => toggleType(t)} /> {t}
                </label>
              ))
            )}
          </div>
          <div style={{ marginTop:8, display:'flex', gap:8, flexWrap:'wrap' }}>
            <button type="button" className="new-layout-btn" onClick={() => loadLogs()} disabled={loadingLogs}>Refresh</button>
            <button type="button" className="new-layout-btn" onClick={handlePurgeRemovedCarts} disabled={clearing || !eventId} title="Delete cart items with status 'removed', 'expired', or 'checkedout' (older than 30 min)">{clearing ? 'Purging…' : 'Purge Stale Cart Items'}</button>
            <button
              type="button"
              className="new-layout-btn"
              onClick={async () => {
                if (!eventId || !layoutId) return;
                setClearing(true);
                setClearResult('');
                try {
                  const n1 = await clearExpiredHolds({ eventId, layoutId });
                  const n2 = await clearExpiredPendingReservations({ eventId, layoutId });
                  const total = (n1 || 0) + (n2 || 0);
                  setClearResult(total === 0 ? 'No expired reservations found.' : `Cleared ${total} expired reservation${total === 1 ? '' : 's'} (${n1 || 0} holds, ${n2 || 0} pending).`);
                  await loadLogs();
                } catch (e) {
                  setClearResult(e?.message || 'Failed to clear expired reservations');
                } finally {
                  setClearing(false);
                }
              }}
              disabled={clearing || !eventId}
              title="Delete expired Pending reservations for this event/layout"
            >
              {clearing ? 'Clearing…' : 'Purge Expired Reservations'}
            </button>
            <button 
              type="button" 
              className="new-layout-btn" 
              onClick={handlePurgeStaleSessions} 
              disabled={purgingStaleSessions}
              title="Delete session states older than 30 minutes"
            >
              {purgingStaleSessions ? 'Purging…' : 'Purge Stale Sessions (>30min)'}
            </button>
            {isAdmin && (
              <>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleMarkDuplicateAttendees}
                  disabled={duplicateActionBusy || !eventId}
                  title="Find duplicate attendees by vendor and name, then mark duplicate records with delete=true"
                  style={{ backgroundColor: '#d32f2f', color: 'white' }}
                >
                  {duplicateActionBusy ? 'Working…' : 'Mark Duplicate Attendees'}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleDeleteDuplicateAttendees}
                  disabled={duplicateActionBusy || !eventId}
                  title="Delete attendee records already marked with delete=true for the selected event"
                  style={{ backgroundColor: '#d32f2f', color: 'white' }}
                >
                  {duplicateActionBusy ? 'Working…' : 'Delete Duplicate Attendees'}
                </button>
              </>
            )}
            {isAdmin && (
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={() => setShowPurgeConfirm(true)} 
                disabled={purgingAll}
                title="⚠️ DANGER: Delete ALL documents from selected collections"
                style={{ backgroundColor: '#d32f2f', color: 'white' }}
              >
                {purgingAll ? 'Purging All…' : '⚠️ PURGE COLLECTIONS'}
              </button>
            )}
            {removedCount != null && (
              <span className="profile-hint" style={{ alignSelf:'center' }}>
                Removed items: {removedCount}
              </span>
            )}
          </div>
          {clearResult && <div className="profile-hint" style={{ marginTop:6 }}>{clearResult}</div>}
          {staleSessionResult && <div className="profile-hint" style={{ marginTop:6 }}>{staleSessionResult}</div>}
          {duplicateActionResult && <div className="profile-hint" style={{ marginTop:6 }}>{duplicateActionResult}</div>}
          {purgeAllResult && <div className="profile-hint" style={{ marginTop:6, color: '#d32f2f', fontWeight: 'bold' }}>{purgeAllResult}</div>}
          {collectionCounts && (
            <div className="profile-hint" style={{ marginTop:6, fontSize: 12 }}>
              Current counts (selected event): {Object.entries(collectionCounts).map(([col, count]) => `${col}: ${typeof count === 'object' ? 'error' : count}`).join(', ')}
            </div>
          )}
        </div>
      </div>
      {error && <div className="profile-error" style={{ marginBottom:8 }}>{error}</div>}
      <div style={{ overflowX:'auto' }}>
        {loadingLogs ? <div>Loading logs…</div> : (
          <table className="profile-table" style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign:'left', padding:8 }}>Time</th>
                <th style={{ textAlign:'left', padding:8 }}>Action</th>
                <th style={{ textAlign:'left', padding:8 }}>Source/Process</th>
                <th style={{ textAlign:'left', padding:8 }}>Booth Number</th>
                <th style={{ textAlign:'left', padding:8 }}>Status Before</th>
                <th style={{ textAlign:'left', padding:8 }}>Vendor</th>
                <th style={{ textAlign:'left', padding:8 }}>Contact</th>
                <th style={{ textAlign:'left', padding:8 }}>Session</th>
                <th style={{ textAlign:'left', padding:8 }}>Meta</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length === 0 ? (
                <tr><td colSpan={9} style={{ padding:12, textAlign:'center', color:'#666' }}>No log entries</td></tr>
              ) : filteredLogs.map(l => (
                <tr key={l.id}>
                  <td style={{ padding:8, borderTop:'1px solid #eee' }}>{formatTs(l.createdAt)}</td>
                  <td style={{ padding:8, borderTop:'1px solid #eee' }}>
                    {(() => {
                      const t = l.type;
                      const tb = l.triggeredBy;
                      // Unified simplified labels per user request
                      if (t === 'cart-added' || t === 'added') return 'Added';
                      if (t === 'cart-removed' || t === 'removed') {
                        // Show specific removal reasons based on triggeredBy
                        if (tb === 'stale-booth-cleanup') return 'Removed (Stale)';
                        if (tb === 'booth-lost-to-another-user') return 'Lost to Another User';
                        if (tb === 'rule-violation-removal') return 'Removed (Rule)';
                        // Else always return Removed value. 
                        return 'Removed';
                      }
                      if (t === 'cart-checkedout') return 'Checkedout';
                      if (t === 'cart-expired') {
                        // Show specific expiry reasons
                        if (tb === 'cart-expired') return 'Expired (Cart)';
                        if (tb === 'payment-timeout') return 'Expired (Payment)';
                        return 'Expired';
                      }
                      if (t === 'expired-cleanup' || t === 'expired-cleanup-on-hold') return 'Expired';
                      if (t === 'expired-cleanup-session-takeover') return 'Expired';
                      if (t === 'session-logout') return 'Session Logout';
                      if (t === 'hold-cleared' || t === 'hold-cleared-group') return 'Hold Cleared';
                      if (t === 'cart-paid' || t === 'paid') return 'Paid'; // keep Paid for clarity
                      return t; // fallback for any future types
                    })()}
                  </td>
                  <td style={{ padding:8, borderTop:'1px solid #eee' }}>
                    {(() => {
                      const tb = l.triggeredBy || '-';
                      // Format common triggeredBy values for better readability
                      const formatted = tb
                        .split('-')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                      return formatted;
                    })()}
                  </td>
                  <td style={{ padding:8, borderTop:'1px solid #eee' }}>{((l.metadata && (l.metadata.boothNumber || l.metadata.boothNo)) || l.boothNumber || l.boothId || '-')}</td>
                  <td style={{ padding:8, borderTop:'1px solid #eee' }}>{l.previousStatus || '-'}</td>
                  <td style={{ padding:8, borderTop:'1px solid #eee' }}>
                    {(() => {
                      const uid = l.triggeredByProfileId || l.previousProfileId;
                      // Prefer vendor referenced directly on the log (previousVendorId or metadata) first
                      const directVid = l.previousVendorId || (l.metadata && (l.metadata.vendorId || l.metadata.vendor)) || l.vendorId || null;
                      if (directVid && vendorMap[directVid]) return vendorMap[directVid].name || '-';
                      // Next, try vendor via the actor's profile
                      if (uid && profileMap[uid] && profileMap[uid].vendorId && vendorMap[profileMap[uid].vendorId]) {
                        return vendorMap[profileMap[uid].vendorId].name || '-';
                      }
                      // If vendor id exists but vendor not loaded, show the id as fallback
                      if (directVid) return directVid;
                      if (uid && profileMap[uid]) {
                        const prof = profileMap[uid];
                        if (prof.vendorId) return prof.vendorId;
                      }
                      return '-';
                    })()}
                  </td>
                  <td style={{ padding:8, borderTop:'1px solid #eee' }}>
                    {(() => {
                      const uid = l.triggeredByProfileId || l.previousProfileId;
                      // Show profile contact if available
                      if (uid && profileMap[uid]) {
                        const prof = profileMap[uid];
                        const first = (prof.firstName || '').trim();
                        const last = (prof.lastName || '').trim();
                        const name = [first, last].filter(Boolean).join(' ');
                        return name || prof.email || prof.id || '-';
                      }
                      // If no profile contact, show vendor contact info if available
                      const directVid = l.previousVendorId || (l.metadata && (l.metadata.vendorId || l.metadata.vendor)) || l.vendorId || null;
                      const vendor = directVid ? vendorMap[directVid] : null;
                      if (vendor) {
                        return vendor.email || vendor.phoneNumber || vendor.vendorNumber || vendor.id || '-';
                      }
                      return '-';
                    })()}
                  </td>
                  <td style={{ padding:8, borderTop:'1px solid #eee' }}>{l.previousSessionId || (l.metadata && l.metadata.sessionId) || '-'}</td>
                  <td style={{ padding:8, borderTop:'1px solid #eee', fontSize:11 }}>
                    {(() => {
                      const meta = l.metadata || {};
                      const parts = [];
                      // Prefer showing remaining time prominently if present
                      if (typeof meta.remainingTimeSeconds === 'number') {
                        parts.push(`remainingTimeSeconds: ${meta.remainingTimeSeconds}`);
                      }
                      for (const k of Object.keys(meta)) {
                        if (k === 'remainingTimeSeconds') continue; // already handled
                        if (meta[k] == null) continue;
                        parts.push(`${k}: ${meta[k]}`);
                      }
                      return parts.join('; ');
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {actorsLoading && (
        <div className="profile-hint" style={{ marginTop: 8 }}>Resolving vendor and contact names…</div>
      )}

      {/* Purge All Confirmation Modal */}
      {showPurgeConfirm && (
        <div className="validation-modal-overlay" onClick={() => setShowPurgeConfirm(false)}>
          <div className="validation-modal" onClick={e => e.stopPropagation()}>
            <div className="validation-modal-header" style={{ backgroundColor: '#d32f2f', color: 'white' }}>
              <h4>⚠️ DANGER: Purge Collections</h4>
              <button onClick={() => setShowPurgeConfirm(false)} style={{ color: 'white' }}>×</button>
            </div>
            <div className="validation-modal-content">
              <p style={{ fontWeight: 'bold', color: '#d32f2f' }}>
                Purge collections for the selected event only. Selected event:
              </p>
              <div style={{ marginBottom: 12 }}>
                <strong>Event:</strong> {eventChoices.find(e => e.eventId === eventId)?.eventName || '(none)'}
                <br />
                <strong>Event ID:</strong> {eventId || '(none)'}
              </div>
              <p style={{ fontWeight: 'bold', color: '#d32f2f' }}>
                Select which collections to purge (permanently delete documents for the selected event):
              </p>
              <div style={{ marginTop: 15, marginBottom: 15 }}>
                {[
                  { key: 'boothReservations', label: 'boothReservations', desc: 'Booth holds and reservations for the event' },
                  { key: 'cartItems', label: 'cartItems', desc: 'Shopping cart records for the event' },
                  { key: 'reservationLogs', label: 'reservationLogs', desc: 'Audit logs for the event' },
                  { key: 'sessionStates', label: 'sessionStates', desc: 'User session data (may not be scoped to event)' },
                  { key: 'transactions', label: 'transactions', desc: 'Payment transaction records for the event' },
                  { key: 'attendees', label: 'attendees', desc: 'Attendee registration records for the event' },
                  { key: 'attendeeCapacity', label: 'attendeeCapacity', desc: 'Purchased attendee capacity records for the event' },
                  { key: 'boothApplications', label: 'boothApplications', desc: 'Booth application records submitted for the event' },
                  { key: 'checkins', label: 'checkins', desc: 'Vendor check-in records for the event' },
                  { key: 'registrations', label: 'registrations', desc: 'Registration records for the event' }
                ].map(({ key, label, desc }) => (
                  <label key={key} style={{ display: 'block', marginBottom: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedCollections[key] || false}
                      onChange={(e) => setSelectedCollections(prev => ({ ...prev, [key]: e.target.checked }))}
                      style={{ marginRight: 8 }}
                    />
                    <strong>{label}</strong> - {desc}
                    {collectionCounts && collectionCounts[key] != null && (
                      <span style={{ marginLeft: 8, color: '#666', fontSize: 13 }}>
                        ({typeof collectionCounts[key] === 'object' ? 'error' : `${collectionCounts[key]} docs`})
                      </span>
                    )}
                  </label>
                ))}
              </div>
              <p style={{ marginTop: 15, fontWeight: 'bold' }}>
                This action CANNOT be undone. Are you absolutely sure?
              </p>
            </div>
            <div className="validation-modal-actions">
              <button onClick={() => setShowPurgeConfirm(false)} className="new-layout-btn">
                Cancel
              </button>
              <button 
                className="purge-confirm-btn"
                onClick={handlePurgeAllCollections} 
                disabled={!eventId || !Object.values(selectedCollections).some(v => v)}
                title={!eventId ? 'Select an event first' : (!Object.values(selectedCollections).some(v => v) ? 'Select at least one collection' : 'Permanently delete selected collections for the event')}
              >
                Yes, Purge Selected Collections
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationLogsViewer;
